/**
 * Parenthesised EXPRESSION positions inside a PL/SQL PROCEDURAL statement —
 * a call's argument list, a parenthesised condition, a type constraint.
 *
 * `BEGIN DBMS_OUTPUT.PUT_LINE(util_pkg.format_currency(▮1500)); END;` is not
 * SQL: the statement never pulls a relation in, so a table / view / schema
 * name is not a legal argument — only literals, binds, PL/SQL locals and
 * other callable expressions are. The resolver's `unknown`-position emission
 * answered with the whole relation list anyway (oracle coverage script.56 §2
 * forbids `<any-table>` / `<any-view>` there).
 *
 * A post-filter, like `parenScopeFilter.ts`: the locals, binds and functions
 * already emitted ARE the right answer, and only the relation-shaped
 * candidates have to go.
 *
 * Scope is an unclosed paren inside a PL/SQL BLOCK whose own statement carries
 * NO SQL verb — `f(▮`, `IF (▮`, `VARCHAR2(▮)`. Both halves are load-bearing:
 * the block requirement keeps every SQL statement's parens out (a
 * `CREATE TABLE … REFERENCES ▮` sits inside the column-list paren; a bare
 * `GRAPH_TABLE(▮` carries no verb either), and the verb check keeps the SQL
 * statements embedded IN a block out. Two guards fall out of that:
 *   - a SQL statement embedded in the block (`SELECT … INTO v FROM t`,
 *     `INSERT INTO audit_log …`, `OPEN c FOR SELECT …`) keeps its relations,
 *     because its own statement window carries the verb;
 *   - a caret that is not inside any paren (the block's statement-start, where
 *     the user is about to type a verb) is untouched, so the statement-start
 *     emission is unchanged.
 *
 * Oracle-only (parity note): PL/SQL is Oracle's procedural dialect. PG's
 * plpgsql has the same property, but its bodies arrive as dollar-quoted
 * regions with their own analysis window (`deriveContext`'s `routineBody`
 * seam) and no coverage row pins the leak there yet; T-SQL / MySQL / SQLite
 * procedural bodies are statement-shaped, not expression-shaped.
 */

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';

import { isInsideRoutineBodyBlock } from '../context/insideRoutineBody';
import { tokenize, type Token } from '../context/tokenizer';

/** Candidate kinds that name a stored relation or its namespace. */
const RELATION_KINDS: ReadonlySet<CompletionCandidate['kind']> = new Set<
  CompletionCandidate['kind']
>(['table', 'view', 'schema', 'database']);

/** Verbs whose presence means the statement IS SQL, relations and all. */
const SQL_VERBS: ReadonlySet<string> = new Set([
  'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'MERGE', 'WITH', 'CURSOR',
]);

export function applyPlsqlParenFilter(
  candidates: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): CompletionCandidate[] {
  if (engine !== 'oracle') return candidates;
  if (!isInsidePlsqlExpressionParen(text, offset, statementStart)) return candidates;
  return candidates.filter((c) => !RELATION_KINDS.has(c.kind));
}

/**
 * True when the caret sits inside an unclosed paren of a PL/SQL block's own
 * procedural statement — a statement with no SQL verb before the caret. The
 * opener must follow a WORD (`f(`, `pkg.f(`, `IF (`, `VARCHAR2(`), which is
 * what every PL/SQL expression paren does; a bare `(` opened by punctuation is
 * left alone.
 */
export function isInsidePlsqlExpressionParen(
  text: string,
  offset: number,
  statementStart: number,
): boolean {
  // The caret must be inside an open `BEGIN … END` — a routine body or an
  // anonymous block. Without this, a statement that merely lacks a SQL verb
  // (`CREATE TABLE … REFERENCES ▮` inside the column list, a degenerate
  // `GRAPH_TABLE(▮`) would be read as procedural and lose the relation names
  // that ARE its answer.
  if (!isInsideRoutineBodyBlock(text, offset, 'oracle')) return false;
  const tokens = tokenize(text, statementStart, offset, 'oracle');
  if (tokens.length === 0) return false;
  for (const t of tokens) {
    const w = wordOf(t);
    if (w !== null && SQL_VERBS.has(w)) return false;
  }
  const lparenIdx = enclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  // The opener must follow a word — a call name, a dotted member, or a
  // control-flow / type keyword. `IF (v > 0)` counts: a relation is no more
  // legal in a PL/SQL condition than in an argument.
  const before = tokens[lparenIdx - 1]!;
  return before.type === 'ident' || before.type === 'kw';
}

/** Index of the innermost `(` still unclosed at the end of the stream, or -1. */
function enclosingLparenIndex(tokens: readonly Token[]): number {
  const stack: number[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i]!.type === 'lparen') stack.push(i);
    else if (tokens[i]!.type === 'rparen') stack.pop();
  }
  return stack.length > 0 ? stack[stack.length - 1]! : -1;
}

function wordOf(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
